import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
import EnergyTrainingChart from "./EnergyTrainingChart";
import { hasZoneData } from "../../data/energyTrainingZones";

/**
 * Energy & Training detail variant — Section 2 sub-step "energy-training".
 * Per Figma nodes 14711:148285 (no zone selected) and 14711:148819 (zone
 * detail card visible above the chart).
 *
 * Data source: `prep.energyTraining.zones` from the `GetConsultation`
 * endpoint (fetched via `fetchConsultationPrep`). The chart itself is
 * `EnergyTrainingChart`, shared with the Health Overview.
 */
const EnergyTrainingPanel: React.FC = () => {
  const theme: Theme = useTheme();
  const { data } = useConsultation();
  const zones = data.prep?.energyTraining?.zones ?? [];

  if (!hasZoneData(zones)) {
    return (
      <Box sx={styles.panel(theme)}>
        <Box sx={styles.headerRow}>
          <Box component="span" sx={styles.title(theme)}>
            Energy &amp; Training
          </Box>
        </Box>
        <Box sx={styles.subtle(theme)}>
          No training-zone data is available for this client yet.
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.panel(theme)}>
      {/* Energy & Training has no aggregate score — it's a presentation of
          the zones, not a scored system. Render only the title. */}
      <Box sx={styles.section24}>
        <Box sx={styles.headerRow}>
          <Box component="span" sx={styles.title(theme)}>
            Energy &amp; Training
          </Box>
        </Box>
      </Box>

      <Box sx={styles.divider(theme)} />

      <EnergyTrainingChart zones={zones} />
    </Box>
  );
};

export default EnergyTrainingPanel;

const styles = {
  panel: (theme: Theme) => ({
    backgroundColor: `${theme.colors_ds.NEUTRAL_BG_MAIN}59`,
    border: `1px solid rgba(0,0,0,0.05)`,
    borderRadius: "16px",
    padding: "16px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "24px",
    minHeight: "100%",
    overflow: "auto",
    boxShadow: "0px 25px 50px -12px rgba(0,0,0,0.05)",
  }),
  section24: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "32px",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "18px",
    lineHeight: "1.4",
    letterSpacing: "-0.36px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  subtle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  divider: (theme: Theme) => ({
    height: "1px",
    width: "100%",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
};
